import {
  clearCsrfTokenCache,
  clearRefreshState,
  getAppConfigQueryOptions,
  resetLogoutState
} from '@treneo/features/config';
import { clearAllCoursesQueries } from '@treneo/features/courses';
import { CURRENT_USER_QUERY_KEY, getCurrentUserQueryOptions } from '@treneo/features/user';
import { queryClient } from '@treneo/infrastructure/query';
import type {
  IChangePasswordRequest,
  IForgotPasswordRequest,
  ILogInAndJoinToCourseRequest,
  ILogInOrSignUpViaGoogleRequest,
  ILogInOrSignUpViaLinkedInRequest,
  ILogInRequest,
  IRegisterAndJoinToCourseRequest,
  IRegisterRequest,
  IResetPasswordRequest
} from '@treneo/models';
import { urls } from '@treneo/shared/constants';
import { logger } from '@treneo/shared/logger';
import { saveAuthMetadataToLocalStorage } from '@treneo/shared/utils';

import { authApi } from '../api';

import { useAuthStore } from './auth-store';
import { LoginState } from './types';

type ApiErrorPayload = {
  detail?: string;
  errorTranslationCode?: string;
  translationKey?: string;
};

const extractTranslationKey = (errorData: unknown): string | null => {
  const payload = errorData as ApiErrorPayload;
  if (payload?.translationKey && typeof payload.translationKey === 'string') {
    return payload.translationKey;
  }
  if (payload?.detail && typeof payload.detail === 'string') {
    return payload.detail;
  }
  if (payload?.errorTranslationCode) {
    return payload.errorTranslationCode;
  }
  return null;
};

const GET_CURRENT_USER_RETRY_ATTEMPTS = 3;
const GET_CURRENT_USER_RETRY_DELAY_MS = 500;

const fetchCurrentUserAndSyncToQuery = async (): Promise<void> => {
  let lastError: unknown;
  for (let attempt = 1; attempt <= GET_CURRENT_USER_RETRY_ATTEMPTS; attempt++) {
    try {
      const userInfo = await queryClient.fetchQuery(getCurrentUserQueryOptions(true));
      queryClient.setQueryData(CURRENT_USER_QUERY_KEY, userInfo);
      return;
    } catch (error: unknown) {
      lastError = error;
      const err = error as { response?: { status?: number } };
      const is401 = err?.response?.status === 401;
      const isRetryable = is401 && attempt < GET_CURRENT_USER_RETRY_ATTEMPTS;
      if (isRetryable) {
        await new Promise(resolve => setTimeout(resolve, GET_CURRENT_USER_RETRY_DELAY_MS));
      } else {
        throw error;
      }
    }
  }
  throw lastError;
};

let isLoggingOut = false;

export const performLogout = async (): Promise<void> => {
  const store = useAuthStore.getState();
  if (isLoggingOut) {
    return;
  }
  isLoggingOut = true;
  store.setLoginState(LoginState.LOGGING_OUT);
  try {
    await authApi.logout();
  } catch (error) {
    logger.warn('Logout API call failed, but continuing with local cleanup:', error);
  } finally {
    isLoggingOut = false;
  }
  clearAllCoursesQueries(queryClient);
  queryClient.removeQueries({ queryKey: CURRENT_USER_QUERY_KEY });
  const { disconnectSignalR } = await import('@treneo/infrastructure/realtime');
  disconnectSignalR();
  clearCsrfTokenCache();
  clearRefreshState();
  resetLogoutState();
  await queryClient.prefetchQuery(getAppConfigQueryOptions());
  store.logoutAction();
};

const handleLoginSuccess = async (): Promise<{ shouldNavigateTo: string }> => {
  await fetchCurrentUserAndSyncToQuery();
  saveAuthMetadataToLocalStorage({
    isAuthenticated: true,
    lastCheck: Date.now()
  });
  const { connectSignalR } = await import('@treneo/infrastructure/realtime');
  globalThis.setTimeout(() => connectSignalR({ skipAuthCheck: true }), 3500);
  return { shouldNavigateTo: urls.homePage };
};

export const login = async (
  request: ILogInRequest
): Promise<{ shouldNavigateTo?: string; error?: string }> => {
  const store = useAuthStore.getState();
  store.setLoginState(LoginState.PENDING);
  store.clearLoginError();
  try {
    await authApi.login(request);
    await handleLoginSuccess();
    store.setLoggedIn();
    return { shouldNavigateTo: urls.homePage };
  } catch (exception: unknown) {
    const err = exception as { response?: { data?: unknown }; message?: string };
    const errorData = err?.response?.data;
    const fallbackKey =
      typeof err?.message === 'string' && err.message.startsWith('auth.')
        ? err.message
        : 'auth.loginFailed';
    const key = extractTranslationKey(errorData) ?? fallbackKey;
    store.setLoginState(LoginState.LOGIN_REJECTED);
    store.setLoginError(key);
    return { error: key };
  }
};

export const loginAndJoinToCourse = async (
  request: ILogInAndJoinToCourseRequest
): Promise<{ shouldNavigateTo?: string; error?: string }> => {
  const store = useAuthStore.getState();
  store.setLoginState(LoginState.PENDING);
  store.clearLoginError();
  try {
    const result = await authApi.loginAndJoinToCourse(request);
    if (result.isSuccess) {
      await handleLoginSuccess();
      store.setLoggedIn();
      return { shouldNavigateTo: urls.homePage };
    }
    const key = extractTranslationKey(result.error) ?? 'auth.loginFailed';
    store.setLoginState(LoginState.LOGIN_REJECTED);
    store.setLoginError(key);
    return { error: key };
  } catch (exception: unknown) {
    const err = exception as { response?: { data?: unknown }; message?: unknown };
    const key = extractTranslationKey(err?.response?.data) ?? 'auth.loginFailed';
    store.setLoginState(LoginState.LOGIN_REJECTED);
    store.setLoginError(key);
    return { error: key };
  }
};

export const logInOrSignUpViaGoogle = async (
  request: ILogInOrSignUpViaGoogleRequest
): Promise<{ shouldNavigateTo?: string; error?: string }> => {
  const store = useAuthStore.getState();
  store.setLoginState(LoginState.PENDING);
  store.clearLoginError();
  try {
    await authApi.loginOrSignUpViaGoogle(request);
    await handleLoginSuccess();
    store.setLoggedIn();
    return { shouldNavigateTo: urls.homePage };
  } catch (exception: unknown) {
    const err = exception as { response?: { data?: unknown }; message?: string };
    const key = extractTranslationKey(err?.response?.data) ?? 'auth.loginFailed';
    store.setLoginState(LoginState.LOGIN_REJECTED);
    store.setLoginError(key);
    return { error: key };
  }
};

export const logInOrSignUpViaLinkedIn = async (
  request: ILogInOrSignUpViaLinkedInRequest
): Promise<{ shouldNavigateTo?: string; error?: string }> => {
  const store = useAuthStore.getState();
  store.setLoginState(LoginState.PENDING);
  store.clearLoginError();
  try {
    await authApi.loginOrSignUpViaLinkedIn(request);
    await handleLoginSuccess();
    store.setLoggedIn();
    return { shouldNavigateTo: urls.homePage };
  } catch (exception: unknown) {
    const err = exception as { response?: { data?: unknown }; message?: string };
    const key = extractTranslationKey(err?.response?.data) ?? 'auth.loginFailed';
    store.setLoginState(LoginState.LOGIN_REJECTED);
    store.setLoginError(key);
    return { error: key };
  }
};

export const register = async (request: IRegisterRequest): Promise<{ error?: string }> => {
  const store = useAuthStore.getState();
  const req = {
    ...request,
    origin: globalThis.window.location.origin + import.meta.env.BASE_URL
  };
  try {
    await authApi.register(req);
    store.setIsRegistrationSuccess(true);
    store.clearRegistrationError();
    return {};
  } catch (exception: unknown) {
    const err = exception as { response?: { data?: unknown } };
    const key = extractTranslationKey(err?.response?.data) ?? 'auth.registrationFailed';
    store.setRegistrationError(key);
    return { error: key };
  }
};

export const registerAndJoinToCourse = async (
  request: IRegisterAndJoinToCourseRequest
): Promise<{ shouldNavigateTo?: string; error?: string }> => {
  const store = useAuthStore.getState();
  store.setLoginState(LoginState.PENDING);
  store.clearRegistrationError();
  try {
    const result = await authApi.registerAndJoinToCourse(request);
    if (result.isSuccess) {
      await handleLoginSuccess();
      store.setLoggedIn();
      store.clearRegistrationError();
      return { shouldNavigateTo: urls.homePage };
    }
    const key = extractTranslationKey(result.error) ?? 'auth.registrationFailed';
    store.setLoginState(LoginState.LOGIN_REJECTED);
    store.setRegistrationError(key);
    return { error: key };
  } catch (exception: unknown) {
    const err = exception as { response?: { data?: unknown } };
    const key = extractTranslationKey(err?.response?.data) ?? 'auth.registrationFailed';
    store.setLoginState(LoginState.LOGIN_REJECTED);
    store.setRegistrationError(key);
    return { error: key };
  }
};

export const confirmEmailAndActivateAccount = async (code: string): Promise<{ error?: string }> => {
  try {
    await authApi.confirmEmailAndActivateAccount(code);
    return {};
  } catch (exception: unknown) {
    const err = exception as { response?: { data?: unknown } };
    const key = extractTranslationKey(err?.response?.data) ?? 'auth.activationFailed';
    return { error: key };
  }
};

export const forgotPassword = async (
  request: IForgotPasswordRequest
): Promise<{ error?: string }> => {
  const store = useAuthStore.getState();
  const req = {
    ...request,
    origin: globalThis.window.location.origin + import.meta.env.BASE_URL
  };
  try {
    await authApi.forgotPassword(req);
    store.setIsForgotPasswordSuccess(true);
    return {};
  } catch (exception: unknown) {
    const err = exception as { response?: { data?: unknown } };
    store.setIsForgotPasswordSuccess(false);
    const key = extractTranslationKey(err?.response?.data) ?? 'auth.forgotPasswordFailed';
    store.setForgotPasswordError(key);
    return { error: key };
  }
};

export const resetPassword = async (
  request: IResetPasswordRequest
): Promise<{
  shouldNavigateTo?: string;
  navigationState?: Record<string, unknown>;
  error?: string;
}> => {
  try {
    await authApi.resetPassword(request);
    return {
      shouldNavigateTo: urls.loginPage,
      navigationState: { isAfterResetPassword: true }
    };
  } catch (exception: unknown) {
    const err = exception as { response?: { data?: unknown } };
    const key = extractTranslationKey(err?.response?.data) ?? 'auth.resetPasswordFailed';
    useAuthStore.getState().setResetPasswordError(key);
    return { error: key };
  }
};

export const changePassword = async (
  request: IChangePasswordRequest
): Promise<{ error?: string }> => {
  const store = useAuthStore.getState();
  try {
    await authApi.changePassword(request);
    store.setIsChangePasswordSuccess(true);
    store.clearChangePasswordError();
    return {};
  } catch (exception: unknown) {
    const err = exception as { response?: { data?: unknown } };
    const key = extractTranslationKey(err?.response?.data) ?? 'auth.changePasswordFailed';
    store.setChangePasswordError(key);
    return { error: key };
  }
};
